import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { H4 } from 'foundations-components/transitional/components/H4';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { View } from 'foundations-components/components/View';
import { Text } from 'foundations-components/components/Text';
import { NOT_FOUND, USER_NOT_FOUND, MEETING_PREVIOUSLY_CANCELLED, REBOOK_CONFLICT } from 'MeetingsLib/constants/errors';
import { getLongDisplayName } from 'MeetingsLib/utils/utils';
import { resetForPaidMeetingBookingFailure } from '../../redux/actions/FormActions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PaymentError = props => {
  const {
    meetingCreationFailed,
    onFailure,
    profile,
    meetingRescheduleError,
    NotFoundMarker,
    ErrorMarker,
    paidMeetingInfo,
    compact
  } = props;
  const dispatch = useDispatch();
  useEffect(() => {
    if (meetingCreationFailed) {
      onFailure();
    }
  }, [meetingCreationFailed, onFailure]);
  const handleResetApp = () => {
    dispatch(resetForPaidMeetingBookingFailure());
  };
  const renderHeader = message => {
    return /*#__PURE__*/_jsx(H4, {
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: message
      })
    }, "header");
  };
  const renderPaidAlert = () => {
    const currencyAndAmount = /*#__PURE__*/_jsx(FormattedIntlCurrency, {
      value: paidMeetingInfo.total,
      currencyCode: paidMeetingInfo.currency
    });
    const paidMessage = /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: "error.general.paidMeetingAlertTitle",
      options: {
        amount: currencyAndAmount
      }
    });
    return /*#__PURE__*/_jsx(Alert, {
      marginInline: "auto",
      inlineSize: compact ? '100%' : '40%',
      titleText: paidMessage,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "error.general.paidMeetingAlertMessage"
      })
    });
  };
  const renderDescription = (message, options = {}) => {
    return /*#__PURE__*/_jsx(View, {
      marginInline: "auto",
      maxInlineSize: "416px",
      children: /*#__PURE__*/_jsx(Text, {
        as: "p",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: message,
          options: options
        })
      }, "description")
    });
  };
  const renderEmail = () => {
    return /*#__PURE__*/_jsx(Text, {
      as: "span",
      color: "text-interactive-default",
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "error.emailLink_jsx",
        options: {
          fullName: getLongDisplayName(profile),
          emailAddress: profile.email,
          linkProps: {
            href: `mailto:${profile.email}`
          }
        },
        elements: {
          Link
        }
      })
    });
  };
  const renderRetryButton = desiredMessage => {
    const retryRebook = meetingRescheduleError === REBOOK_CONFLICT;
    const message = desiredMessage || 'error.button';
    return /*#__PURE__*/_jsx(View, {
      marginBlockStart: "900",
      marginBlockEnd: "1200",
      children: /*#__PURE__*/_jsx(Button, {
        use: retryRebook ? 'primary' : 'tertiary',
        onClick: handleResetApp,
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: message
        })
      })
    }, "retry");
  };
  const renderCreateErrorWithReload = () => {
    return /*#__PURE__*/_jsxs(View, {
      children: [renderPaidAlert(), /*#__PURE__*/_jsxs(ErrorMessage, {
        title: renderHeader('error.general.header'),
        children: [renderDescription('error.general.retryDescription'), renderRetryButton(), renderEmail()]
      })]
    });
  };
  const renderRescheduleErrorWithReload = (descriptionMessage, buttonMessage) => {
    const desiredDescription = descriptionMessage || 'error.general.retryDescription';
    return /*#__PURE__*/_jsxs(ErrorMessage, {
      title: renderHeader('error.general.header'),
      children: [renderDescription(desiredDescription), renderRetryButton(buttonMessage), renderEmail()]
    });
  };
  const renderRescheduleConflict = () => {
    const buttonMessage = 'error.buttonRebookConflict';
    const desiredDescription = 'error.general.rebookConflictError';
    return /*#__PURE__*/_jsxs(ErrorMessage, {
      title: renderHeader('error.general.header'),
      illustration: "meetings",
      children: [renderDescription(desiredDescription), renderRetryButton(buttonMessage)]
    });
  };
  const renderInactiveState = () => {
    return /*#__PURE__*/_jsxs(ErrorMessage, {
      type: "notFound",
      title: renderHeader('error.downgraded.header'),
      children: [renderDescription('error.downgraded.description'), renderEmail(), /*#__PURE__*/_jsx(NotFoundMarker, {})]
    });
  };
  const renderLinkNotFound = () => {
    return /*#__PURE__*/_jsxs(ErrorMessage, {
      "data-test-id": "link-not-found",
      type: "notFound",
      title: renderHeader('error.general.header'),
      children: [renderDescription('error.general.genericDescription'), /*#__PURE__*/_jsx(NotFoundMarker, {})]
    });
  };
  const renderError = () => {
    return /*#__PURE__*/_jsxs(ErrorMessage, {
      children: [renderDescription('error.general.errorDescription'), /*#__PURE__*/_jsx(ErrorMarker, {})]
    });
  };
  const renderRescheduleFailure = () => {
    const rebookErrorMessage = 'error.general.rebookGeneralError';
    const rebookButtonMessage = 'error.buttonRebook';
    switch (meetingRescheduleError) {
      case MEETING_PREVIOUSLY_CANCELLED:
        return renderRescheduleErrorWithReload(rebookErrorMessage, rebookButtonMessage);
      case REBOOK_CONFLICT:
        return renderRescheduleConflict();
      default:
        return renderCreateErrorWithReload();
    }
  };
  const renderDifferentMessages = () => {
    const {
      bookInfoFailed,
      bookInfoError,
      meetingsUserFetchSuccess,
      meetingRescheduleFailed
    } = props;
    if (meetingRescheduleFailed) {
      return renderRescheduleFailure();
    }
    if (meetingCreationFailed) {
      return renderCreateErrorWithReload();
    }
    if (bookInfoFailed && (bookInfoError === NOT_FOUND || bookInfoError === USER_NOT_FOUND)) {
      if (meetingsUserFetchSuccess) {
        return renderInactiveState();
      }
      return renderLinkNotFound();
    }
    return renderError();
  };
  return /*#__PURE__*/_jsx(View, {
    padding: "1500",
    marginInline: "auto",
    textAlign: "center",
    "data-selenium-test": "payment-error-container",
    children: renderDifferentMessages()
  });
};
export default PaymentError;